import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { PlusIcon, TrashIcon, ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import type { IMInstanceSummary, IMPlatform } from '../../types/im';
import IMSettings from './IMSettings';

const platforms: Array<[IMPlatform, string]> = [['dingtalk', '钉钉'], ['feishu', '飞书'], ['wechatwork', '企业微信'], ['qq', 'QQ'], ['wechat', '微信'], ['telegram', 'Telegram'], ['discord', 'Discord']];

/** 实例导航与生命周期页面；机器人配置仍复用 IMSettings，不复制各平台表单。 */
export default function IMInstancesPanel() {
  const [instances, setInstances] = useState<IMInstanceSummary[]>([]);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<'create' | 'delete' | null>(null);
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<IMPlatform>('dingtalk');
  const [, setLanguage] = useState(i18nService.getLanguage());
  const generation = useRef(0), locked = useRef(false);
  const current = instances.find(item => item.id === selected);
  const t = (key: string) => i18nService.t(key);

  useEffect(() => i18nService.subscribe(() => setLanguage(i18nService.getLanguage())), []);
  useEffect(() => {
    const epoch = ++generation.current;
    setBusy(true);
    const api = window.electron.im.listInstances;
    (api ? api() : Promise.resolve([{ id: 'default', name: t('imInstancesDefault'), platform: 'all' as const, enabled: false }])).then(rows => {
      if (epoch !== generation.current) return;
      setInstances(rows); setSelected(rows[0]?.id ?? '');
    }).catch(reason => {
      if (epoch === generation.current) setError(reason instanceof Error ? reason.message : t('imInstancesFailed'));
    }).finally(() => { if (epoch === generation.current) setBusy(false); });
    const invalidate = () => { generation.current++; setInstances([]); setSelected(''); setDialog(null); setName(''); setError(''); };
    window.addEventListener('auth:identityChanging', invalidate);
    return () => { generation.current++; window.removeEventListener('auth:identityChanging', invalidate); };
  }, []);

  /** 明确确认后再创建/删除；锁定操作对象，列表刷新失败不重复执行变更。 */
  const confirm = async () => {
    if (locked.current || !dialog) return;
    const epoch = generation.current, id = selected, action = dialog;
    locked.current = true; setBusy(true); setError('');
    try {
      if (action === 'create') {
        const create = window.electron.im.createInstance;
        if (!create) throw new Error(t('imInstancesUnsupported'));
        const item = await create(name.trim(), platform);
        if (epoch !== generation.current) return;
        setInstances(rows => [...rows, item]); setSelected(item.id); setName('');
      } else {
        const remove = window.electron.im.deleteInstance;
        if (!remove) throw new Error(t('imInstancesUnsupported'));
        const result = await remove(id);
        if (epoch !== generation.current) return;
        if (!result.success) throw new Error(t('imInstancesFailed'));
        setInstances(rows => rows.filter(item => item.id !== id));
        setSelected(instances.find(item => item.id !== id)?.id ?? '');
      }
      setDialog(null);
    } catch (reason) {
      if (epoch === generation.current) setError(reason instanceof Error ? reason.message : t('imInstancesFailed'));
    } finally {
      locked.current = false;
      if (epoch === generation.current) setBusy(false);
    }
  };

  return <section className="flex min-w-0 flex-col gap-4" aria-label={t('imInstancesTitle')}>
    <div className="rounded-2xl border border-claude-border bg-claude-surface p-4 dark:border-claude-darkBorder dark:bg-claude-darkSurface">
      <div className="flex items-center gap-2 font-semibold"><ChatBubbleLeftRightIcon aria-hidden="true" className="h-5 w-5 text-claude-accent" />{t('imInstancesTitle')}</div>
      <p className="my-2 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{t('imInstancesHint')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label={t('imInstancesTitle')} value={selected} disabled={busy || !!dialog} onChange={event => { setSelected(event.target.value); setError(''); }} className="min-w-0 flex-1 rounded-lg border border-claude-border bg-transparent px-3 py-2 dark:border-claude-darkBorder">
          {instances.map(item => <option key={item.id} value={item.id}>{item.platform === 'all' ? t('imInstancesDefault') : item.name}</option>)}
        </select>
        <button type="button" disabled={busy} onClick={() => { setDialog('create'); setError(''); }} className="flex items-center gap-1 rounded-lg border px-3 py-2 text-sm disabled:opacity-50"><PlusIcon className="h-4 w-4" />{t('imInstancesAdd')}</button>
        {current?.platform !== 'all' && current && <button type="button" disabled={busy} aria-label={t('imInstancesDelete')} onClick={() => { setDialog('delete'); setError(''); }} className="rounded-lg p-2 text-red-600 disabled:opacity-50"><TrashIcon className="h-5 w-5" /></button>}
      </div>
    </div>
    {error && !dialog && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {busy && !current && <p role="status">{t('loading')}</p>}
    {current && <IMSettings key={current.id} instanceId={current.id} platform={current.platform} />}
    <Dialog open={dialog !== null} onClose={() => { if (!busy) setDialog(null); }} className="relative z-[160]" onKeyDown={event => { if (event.key === 'Escape') event.stopPropagation(); }}>
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-2xl border border-claude-border bg-claude-bg p-5 text-claude-text shadow-xl dark:border-claude-darkBorder dark:bg-claude-darkBg dark:text-claude-darkText">
          <div className="flex items-center justify-between"><Dialog.Title className="font-semibold">{t(dialog === 'delete' ? 'imInstancesDelete' : 'imInstancesAdd')}</Dialog.Title><button type="button" disabled={busy} aria-label={t('close')} onClick={() => setDialog(null)}><XMarkIcon className="h-5 w-5" /></button></div>
          {dialog === 'delete' ? <Dialog.Description className="my-4 text-sm">{t('imInstancesDeleteHint')} <strong>{current?.name}</strong></Dialog.Description> : <div className="my-4 space-y-3">
            <label className="block text-sm">{t('imInstancesName')}<input value={name} maxLength={60} disabled={busy} onChange={event => setName(event.target.value)} className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2" /></label>
            <label className="block text-sm">{t('imInstancesPlatform')}<select value={platform} disabled={busy} onChange={event => setPlatform(event.target.value as IMPlatform)} className="mt-1 w-full rounded-lg border bg-transparent px-3 py-2">{platforms.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
          </div>}
          {error && dialog && <p role="alert" className="my-2 text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2"><button type="button" disabled={busy} onClick={() => setDialog(null)} className="rounded-lg border px-3 py-2">{t('cancel')}</button><button type="button" disabled={busy || (dialog === 'create' && !name.trim())} onClick={() => void confirm()} className="rounded-lg bg-claude-accent px-3 py-2 text-white disabled:opacity-50">{t(dialog === 'delete' ? 'confirmDelete' : 'imInstancesAdd')}</button></div>
        </Dialog.Panel>
      </div>
    </Dialog>
  </section>;
}
